function resolveSourceItem(item, allItems) {
  if (item && item.ref && item.content) {
    return (allItems || []).find((i) => String(i.id) === String(item.content)) || item;
  }
  return item;
}

function stripHtml(html) {
  return String(html || '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function getItemDisplayTitle(item, allItems) {
  const source = resolveSourceItem(item, allItems);
  if (source.name) return source.name;
  if (source.type === 'file' && source.content) {
    const url = source.content;
    const fileName = decodeURIComponent((url.split('/').pop() || '').split('?')[0]) || '未命名文件';
    const ext = fileName.includes('.') ? fileName.split('.').pop().toLowerCase() : '';
    if (source.name && ext) return `${source.name}.${ext}`;
    return fileName;
  }
  if (source.type === 'text' || source.type === 'md' || source.type === 'html') {
    const content = source.content || '';
    const firstLine = content.split('\n')[0] || '未命名文档';
    return firstLine.substring(0, 40) + (firstLine.length > 40 ? '…' : '');
  }
  return '未命名';
}

export function resolveItemTextForDecompose(item, allItems) {
  const source = resolveSourceItem(item, allItems);
  if (!source) return null;
  if (source.type === 'md') {
    return (source.content || source.markdown_content || '').trim() || null;
  }
  if (source.type === 'text') {
    return (source.content || '').trim() || null;
  }
  if (source.type === 'html') {
    const text = stripHtml(source.content || '');
    return text || null;
  }
  if (source.type === 'file') {
    const md = (source.markdown_content || '').trim();
    return md || null;
  }
  return null;
}

export function isDecomposableItem(item, allItems) {
  const text = resolveItemTextForDecompose(item, allItems);
  return !!(text && text.length >= 20);
}

export function getDecomposeBlockReason(item, allItems) {
  const source = resolveSourceItem(item, allItems);
  if (!source) return '无效条目';
  if (source.type === 'file' && !(source.markdown_content || '').trim()) {
    return '请先解析为 Markdown';
  }
  const text = resolveItemTextForDecompose(item, allItems);
  if (!text) return '无可用文本';
  if (text.length < 20) return '内容过短';
  return '';
}

function escapeYaml(value) {
  return String(value || '').replace(/"/g, '\\"');
}

export function buildDecomposePrompt(title, content) {
  const truncated = content.length > 12000
    ? `${content.slice(0, 12000)}\n\n…（内容已截断）`
    : content;
  return `你是知识库拆解助手。请将以下文档拆解为若干独立知识点，每个知识点应完整、可独立阅读。

只返回 JSON 数组，不要任何解释或 markdown 代码块。每项格式：
{"title":"知识点标题","summary":"一句话摘要","content":"正文（Markdown）","quote":"原文关键摘录（可选，50字内）"}

要求：
- 拆解 3～15 个知识点，按原文逻辑顺序
- 不重复、覆盖重要信息
- title 简洁明确

文档标题：${title}

文档内容：
${truncated}`;
}

export function cleanAiJsonOutput(raw) {
  if (raw == null) return '';
  let s = String(raw).trim();
  s = s.replace(/^\uFEFF/, '');
  s = s.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();

  const firstBracket = s.indexOf('[');
  const lastBracket = s.lastIndexOf(']');
  const firstBrace = s.indexOf('{');
  const lastBrace = s.lastIndexOf('}');

  // Prefer JSON array when it appears as the root payload.
  if (firstBracket >= 0 && lastBracket > firstBracket) {
    if (firstBrace < 0 || firstBracket <= firstBrace) {
      return s.slice(firstBracket, lastBracket + 1).trim();
    }
  }
  if (firstBrace >= 0 && lastBrace > firstBrace) {
    return s.slice(firstBrace, lastBrace + 1).trim();
  }
  return s.trim();
}

function tryParseJsonValue(raw) {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw;
  let s = String(raw).trim();
  if (!s) return null;

  const candidates = [cleanAiJsonOutput(s), s];
  for (let i = 0; i < candidates.length; i += 1) {
    let piece = candidates[i];
    if (!piece) continue;
    for (let depth = 0; depth < 3; depth += 1) {
      try {
        const parsed = JSON.parse(piece);
        if (typeof parsed === 'string') {
          piece = parsed.trim();
          continue;
        }
        return parsed;
      } catch (e) {
        break;
      }
    }
  }
  return null;
}

function pickPointField(row, keys) {
  if (!row || typeof row !== 'object') return '';
  for (let i = 0; i < keys.length; i += 1) {
    const v = row[keys[i]];
    if (v != null && String(v).trim()) return String(v).trim();
  }
  return '';
}

function normalizePointList(data) {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (typeof data !== 'object') return [];

  const nestedKeys = [
    'points',
    'items',
    'knowledge_points',
    'knowledgePoints',
    'list',
    'data',
    'result',
    'results',
  ];
  for (let i = 0; i < nestedKeys.length; i += 1) {
    const nested = data[nestedKeys[i]];
    if (Array.isArray(nested)) return nested;
    if (typeof nested === 'string') {
      const parsed = tryParseJsonValue(nested);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') return [parsed];
    }
  }

  // Single knowledge-point object from the model.
  if (
    data.title != null
    || data.summary != null
    || data.content != null
    || data.quote != null
    || data.name != null
  ) {
    return [data];
  }
  return [];
}

function mapDecomposeRows(rows) {
  return (rows || [])
    .map((row, index) => {
      if (typeof row === 'string') {
        const text = row.trim();
        if (!text) return null;
        return {
          title: `知识点 ${index + 1}`,
          summary: '',
          content: text,
          quote: '',
          selected: true,
        };
      }
      if (!row || typeof row !== 'object') return null;
      const title = pickPointField(row, ['title', 'name', 'heading', '标题', '知识点'])
        || `知识点 ${index + 1}`;
      const summary = pickPointField(row, ['summary', 'desc', 'description', '摘要', '简介']);
      const content = pickPointField(row, ['content', 'body', 'text', 'markdown', 'md', '正文', '内容'])
        || summary;
      const quote = pickPointField(row, ['quote', 'excerpt', 'source', '摘录', '原文']);
      if (!title || !content) return null;
      return {
        title,
        summary: summary === content ? '' : summary,
        content,
        quote,
        selected: true,
      };
    })
    .filter(Boolean);
}

function fallbackPointsFromText(raw) {
  const text = String(raw || '')
    .replace(/^\uFEFF/, '')
    .replace(/^```(?:json)?\s*/i, '')
    .replace(/\s*```$/i, '')
    .trim();
  if (!text) return [];
  return [{
    title: '拆解结果',
    summary: '',
    content: text,
    quote: '',
    selected: true,
  }];
}

export function parseDecomposeResult(raw) {
  if (raw == null) return [];
  if (typeof raw === 'object') {
    const mapped = mapDecomposeRows(normalizePointList(raw));
    if (mapped.length) return mapped;
  }

  const text = String(raw).trim();
  if (!text) return [];

  const parsed = tryParseJsonValue(text);
  if (parsed != null) {
    const mapped = mapDecomposeRows(normalizePointList(parsed));
    if (mapped.length) return mapped;
  }

  // Model returned plain text / incomplete JSON — still show as one point.
  return fallbackPointsFromText(text);
}

export function buildKnowledgePointMd(point, meta) {
  const {
    sourceId,
    sourceName,
    splitGroup,
    splitIndex,
    splitTotal,
    siblingTitles,
  } = meta;

  const frontmatter = [
    '---',
    `source_id: "${sourceId}"`,
    `source_name: "${escapeYaml(sourceName)}"`,
    `split_group: "${splitGroup}"`,
    `split_index: ${splitIndex}`,
    `split_total: ${splitTotal}`,
    '---',
    '',
  ].join('\n');

  let body = `# ${point.title}\n\n`;
  if (point.summary) {
    body += `> ${point.summary}\n\n`;
  }
  body += `${point.content}\n\n`;

  if (point.quote) {
    body += `## 原文摘录\n\n> ${point.quote}\n\n`;
  }

  body += '---\n\n';
  body += '**关联信息**\n\n';
  body += `- 来源文档：${sourceName}（ID: ${sourceId}）\n`;
  body += `- 拆解序号：${splitIndex} / ${splitTotal}\n`;
  if (siblingTitles && siblingTitles.length) {
    body += `- 同组知识点：${siblingTitles.join('、')}\n`;
  }

  return frontmatter + body;
}

export function extractAiCompletionText(res) {
  const body = res && res.data != null ? res.data : res;
  if (!body) return '';
  if (typeof body === 'string') return body;
  const data = body.data != null ? body.data : body;
  if (typeof data === 'string') return data;
  if (data && typeof data.content === 'string') return data.content;
  if (data && typeof data.text === 'string') return data.text;
  if (data && typeof data.a === 'string') return data.a;
  if (data && typeof data.result === 'string') return data.result;
  if (Array.isArray(data)) return JSON.stringify(data);
  if (data && typeof data === 'object') {
    const choices = data.choices;
    if (choices && choices[0]) {
      const msg = choices[0].message || choices[0].delta;
      if (msg && typeof msg.content === 'string') return msg.content;
    }
    // Already-parsed JSON object / point payload from completions API.
    try {
      return JSON.stringify(data);
    } catch (e) {
      return '';
    }
  }
  return '';
}

export function newSplitGroupId() {
  return `split-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}
